import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, Plus, Edit2, Trash2 } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';
import { getPayrollCalendarTypeColor, getPayrollCalendarStatusColor } from '../../utils/statusColors';

interface PayrollCalendar {
  _id: string;
  name: string;
  calendarType: 'weekly' | 'fortnightly' | 'four_weekly' | 'monthly' | 'quarterly' | 'twice_monthly' | 'annual';
  xeroCalendarId?: string;
  syncedFromXero: boolean;
  isActive: boolean;
  startDate?: string;
  paymentDate?: string;
  periodStartDate?: string;
  periodEndDate?: string;
}

export const PayrollCalendars: React.FC = () => {
  const { t } = useTranslation();
  const [calendars, setCalendars] = useState<PayrollCalendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/payroll-calendars`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setCalendars(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch payroll calendars');
      }
    } catch (err) {
      console.error('Fetch payroll calendars error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatCalendarType = (type: string) => {
    return type.replace('_', ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return formatDateUtil(dateString);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
      <div className="p-6 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Pay Calendars
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage payroll periods and payment schedules
            </p>
          </div>
        </div>
        <button
          onClick={fetchCalendars}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading pay calendars...</p>
          </div>
        ) : calendars.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">No pay calendars found</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('admin.syncFromXero') || 'Sync from Xero to import pay calendars'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Current Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Payment Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {calendars.map((calendar) => (
                  <tr key={calendar._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {calendar.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPayrollCalendarTypeColor()}`}>
                        {formatCalendarType(calendar.calendarType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {calendar.periodStartDate && calendar.periodEndDate ? (
                        <div>
                          <div>{formatDate(calendar.periodStartDate)} -</div>
                          <div>{formatDate(calendar.periodEndDate)}</div>
                        </div>
                      ) : (
                        'Not set'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(calendar.paymentDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {calendar.syncedFromXero ? (
                        <span className="inline-flex items-center text-xs text-accent-600 dark:text-accent-400">
                          <img src="/xero.svg" alt="Xero" className="w-4 h-4 mr-1" />
                          Xero
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Manual</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPayrollCalendarStatusColor(calendar.isActive)}`}>
                        {calendar.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

